import { Component, computed, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { NgTemplateOutlet } from '@angular/common';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { AvatarComponent } from '../../../../parts/avatar/avatar.component';
import { TooltipDirective } from '../../../../core/directives/tooltip.directive';
import { AvatarConstants } from '../../../../core/constants/avatar.constants';
import { MemberModel } from '../../../../core/models/data/member.model';
import { MemberUtilities } from '../../../../core/utilities/member.utilities';
import { CountryCodePipe, PhoneExtensionPipe, PhonePipe } from '../../../../core/pipes/phone-number/phone-number.pipe';

@Component({
	selector: 'ms-dashboard-member-profile-card',
	imports: [RouterLink, NgTemplateOutlet, FaDuotoneIconComponent, AvatarComponent, TooltipDirective],
	templateUrl: './dashboard-member-profile-card.component.html',
	styleUrl: './dashboard-member-profile-card.component.scss'
})
export class DashboardMemberProfileCardComponent {

	//#region Inputs

	// The org-scoped Member record for the active organization, never the global User — the card links
	// to the Member Profile and has to agree with it.
	public member = input<MemberModel | undefined>(undefined);
	public profileLink = input<string | undefined>(undefined);
	public isLoading = input<boolean>(false);

	//#endregion

	//#region Constants

	public readonly avatarSize: number = AvatarConstants.SIZE().MEMBER_CARD;
	public readonly avatarShape: string = AvatarConstants.SHAPE().MEMBER_CARD;
	public readonly avatarAdditionalAttributes: string = AvatarConstants.ADDITIONAL_ATTRIBUTES().MEMBER_CARD;

	//#endregion

	//#region Computed

	public fullName = computed<string>(() => {
		const member = this.member();
		return member ? MemberUtilities.fullName(member) : '';
	});

	// Country code, number and extension as one string, so the same text feeds both the rendered line
	// and its truncation tooltip. The pipes take no dependencies, so this is the same formatting the
	// templates apply elsewhere rather than a second implementation of it.
	public phoneLine = computed<string>(() => {
		const member = this.member();
		if (!member?.phoneNumber) return '';

		return [
			DashboardMemberProfileCardComponent.countryCodePipe.transform(member.phoneCountryCode),
			DashboardMemberProfileCardComponent.phonePipe.transform(member.phoneNumber),
			DashboardMemberProfileCardComponent.phoneExtensionPipe.transform(member.phoneExtension)
		].filter(part => part.length > 0).join(' ');
	});

	//#endregion

	private static readonly phonePipe = new PhonePipe();
	private static readonly countryCodePipe = new CountryCodePipe();
	private static readonly phoneExtensionPipe = new PhoneExtensionPipe();
}
